import React from 'react';
import { ITableColumns } from 'models/table';
import { Checkbox } from '@mui/material';
import { ILegalEntityExtendedAddress } from 'models/legalEntity';
import { findInDictionary } from 'helpers/dictionary';
import { ILicenceSafeStorage, ISafeStorage } from 'models/licenceConfigurator';
import { IDictionary } from 'models/dictionary';
import { formatGPSField } from 'utils/form';

interface ILegalEntityAddressTableColumns {
  selectedStorage: ILicenceSafeStorage[];
  setSelectedStorage: (storage: ILicenceSafeStorage[]) => void;
  t: (s: string) => string;
}

export const mapLEExtendedAddressToSafeStorage = (
  items: ILegalEntityExtendedAddress[],
  region: IDictionary[] | null,
  country: IDictionary[] | null,
  type: IDictionary[] | null,
  legalEntityId: string,
) =>
  items
    .map((item) => ({
      id: item.uniqueId,
      addressUniqueId: item.uniqueId,
      typeUniqueId: item.typeUniqueId,
      typeName: findInDictionary(item.typeUniqueId, type),
      line: `${item.line1 || ''} ${item.line2 || ''}`,
      line1: item.line1 || '',
      line2: item.line2 || '',
      city: item.city,
      postalCode: item.postalCode,
      regionUniqueId: item.regionUniqueId,
      regionName: findInDictionary(item.regionUniqueId, region),
      countryUniqueId: item.countryUniqueId,
      countryName: findInDictionary(item.countryUniqueId, country),
      gps: formatGPSField(item.latitude, item.longitude),
      latitude: item.latitude,
      longitude: item.longitude,
      isDefault: item.isDefault,
      storage: item.storage,
      firearmAmount: item.firearmAmount,
      ancillaryAmount: item.ancillaryAmount,
      legalEntityId,
    }))
    .filter((address) => address?.storage);

const mapDataToStorage = (storageData: Partial<ISafeStorage>): ILicenceSafeStorage => {
  const storage: ILicenceSafeStorage = {
    addressUniqueId: '',
    typeUniqueId: '',
    line1: '',
    line2: '',
    city: '',
    postalCode: '',
    regionUniqueId: '',
    countryUniqueId: '',
    latitude: 0,
    longitude: 0,
    storage: true,
    isDefault: false,
  };

  (Object.keys(storage) as (keyof ISafeStorage)[]).forEach((key) => {
    if (storageData[key] !== undefined) {
      (storage as any)[key] = storageData[key];
    }
  });

  return storage;
};

export const safeStorageTableColumns = ({
  selectedStorage,
  setSelectedStorage,
  t,
}: ILegalEntityAddressTableColumns): ITableColumns[] => [
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f511',
      key: 'select',
      title: t('legalEntityDetails.storage.select.column'),
      isSortable: false,
      render: (_data, props, index) => {
        const targetItem = mapDataToStorage(props);
        const isChecked = selectedStorage.some(
          (item) => item.addressUniqueId === targetItem.addressUniqueId,
        );

        return (
          <Checkbox
            name={`safeStorage${index}`}
            checked={isChecked}
            onChange={(e) => {
              if (e.target.checked) {
                const newSelectedStorage = selectedStorage.filter(
                  (item) => item.addressUniqueId !== targetItem.addressUniqueId,
                );
                setSelectedStorage([...newSelectedStorage, targetItem]);
              } else {
                setSelectedStorage(
                  selectedStorage.filter(
                    (item) => item.addressUniqueId !== targetItem.addressUniqueId,
                  ),
                );
              }
            }}
          />
        );
      },
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f512',
      key: 'line',
      title: 'legalEntityDetails.addressTab.address.column',
      isSortable: false,
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f513',
      key: 'city',
      title: 'legalEntityDetails.addressTab.city.column',
      isSortable: false,
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f514',
      key: 'regionName',
      title: 'legalEntityDetails.addressTab.stateProvince.column',
      isSortable: false,
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f515',
      key: 'countryName',
      title: 'legalEntityDetails.addressTab.country.column',
      isSortable: false,
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f516',
      key: 'gps',
      title: 'legalEntityDetails.addressTab.gps.column',
      isSortable: false,
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f517',
      key: 'postalCode',
      title: 'legalEntityDetails.addressTab.zipPostalCode.column',
      isSortable: false,
    },
  ];
